import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Mail } from 'lucide-react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
} from '@/components/ui'
import { usePostListUsers } from '@/features/user'
import { useGetRoles } from '@/features/user-management'
import { checkDuplicateEmailsInBatch } from '@/features/user-management/utils'
import { EScopeLevel } from '@/schema'
import { validateEmail } from '@/utils'
import { MAX_TEXT_LENGTH } from '@/utils/constants'

import { useDeliverableSetsStore } from '../store'
import { IInviteUserFormData } from '../types'

const InviteUsersDialog: React.FC = () => {
  const {
    showInviteUserDialog,
    inviteUsers,
    inviteUserSearchText,
    setInviteUserDialogOpen,
    setInviteUsers,
  } = useDeliverableSetsStore()

  const { mutate: postListUsers, isPending } = usePostListUsers()

  const emailAlreadyInvitedRef = React.useRef(false)
  const emailDuplicateRef = React.useRef(false)

  /**
   * Get roles for the deliverable set
   */
  const { roles, availableRoles, reviewerRole } = useGetRoles({
    enabled: showInviteUserDialog,
    scopeLevel: EScopeLevel.DELIVERABLE_SET,
  })

  /**
   * Use form to manage the invite user data
   */
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
    setValue,
    watch,
  } = useForm<IInviteUserFormData>({
    defaultValues: { firstName: '', lastName: '', email: '', roleId: '' },
    mode: 'onChange',
  })

  /**
   * Watch the email field
   */
  const emailWatch = watch('email')

  /**
   * Reset the form when the dialog is closed
   */
  React.useEffect(() => {
    if (!showInviteUserDialog) reset()
  }, [reset, showInviteUserDialog])

  /**
   * Set the email field when the search text is changed
   */
  React.useEffect(() => {
    if (!showInviteUserDialog || !inviteUserSearchText) return

    setValue('email', inviteUserSearchText)
  }, [inviteUserSearchText, setValue, showInviteUserDialog])

  /**
   * Set the role field when the roles are loaded
   */
  React.useEffect(() => {
    if (!showInviteUserDialog || !roles.length) return

    if (reviewerRole) {
      setValue('roleId', reviewerRole.id)
      setValue('roleName', reviewerRole.role_name)
    }
  }, [roles.length, setValue, showInviteUserDialog, reviewerRole])

  /**
   * Validate the email format
   */
  const emailFormatError = React.useMemo(
    () => validateEmail(emailWatch),
    [emailWatch]
  )

  /**
   * Handle the open change event
   */
  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setInviteUserDialogOpen(newOpen)
    },
    [setInviteUserDialogOpen]
  )

  /**
   * Handle the email error
   */
  const handleEmailError = React.useCallback(() => {
    let errorMsg = ''

    // Check if the email format is invalid
    if (emailFormatError) {
      errorMsg = emailFormatError
    } else if (emailDuplicateRef.current) {
      // Check if the email is already in the batch
      errorMsg = 'This email is duplicated'
    } else if (emailAlreadyInvitedRef.current) {
      // Check if the email is already invited
      errorMsg = 'This email already exists'
    }

    return errorMsg ? (
      <p className="mt-1 text-sm text-red-500">{errorMsg}</p>
    ) : null
  }, [emailFormatError])

  /**
   * Handle the submit event
   */
  const onSubmit = (data: IInviteUserFormData) => {
    postListUsers(
      { data: { q: data.email, limit: 1 } },
      {
        onSuccess: ({ data: users }) => {
          // Check if the email is already invited
          if (users.length) {
            emailAlreadyInvitedRef.current = true
            return
          }

          // Check if the email is already in the batch
          const { emailCounts } = checkDuplicateEmailsInBatch(inviteUsers)
          const hasDuplicate =
            emailCounts.get(data.email.toLowerCase().trim()) || false
          if (hasDuplicate) {
            emailDuplicateRef.current = true
            return
          }

          // Add the invite user to the store
          setInviteUsers([
            ...inviteUsers,
            {
              uuid: crypto.randomUUID(),
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              roleId: data.roleId,
              roleName: data.roleName,
            },
          ])
          // Close the dialog
          handleOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog open={showInviteUserDialog} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-semibold text-lg text-primary-900 m-0">
            Add User
          </DialogTitle>
          <DialogDescription hidden />
        </DialogHeader>
        <form
          id="invite-user-deliverable-set-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            {/* First name */}
            <div className="space-y-1">
              <label htmlFor="firstName" className="text-sm font-medium">
                First name
              </label>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    id="firstName"
                    className="bg-white border-solid rounded-md px-lg py-md gap-md border-primary text-md"
                    placeholder="Enter first name"
                    maxLength={MAX_TEXT_LENGTH}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/* Last name */}
            <div className="space-y-1">
              <label htmlFor="lastName" className="text-sm font-medium">
                Last name
              </label>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    id="lastName"
                    className="bg-white border-solid rounded-md px-lg py-md gap-md border-primary text-md"
                    placeholder="Enter last name"
                    maxLength={MAX_TEXT_LENGTH}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Controller
              name="email"
              rules={{ required: 'Email is required' }}
              control={control}
              render={({ field }) => (
                <Input
                  id="email"
                  type="email"
                  className="bg-white border-solid rounded-md px-lg py-md gap-md border-primary text-md"
                  placeholder="Enter email"
                  maxLength={MAX_TEXT_LENGTH}
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val)
                    emailAlreadyInvitedRef.current = false
                  }}
                  leadingIcon={
                    <Mail
                      size={16}
                      className="size-4 text-foreground-fg-quaternary-400"
                    />
                  }
                />
              )}
            />
            {handleEmailError()}
          </div>

          {/* Role */}
          <div className="space-y-1.5">
            <label
              htmlFor="role"
              className="text-sm font-medium text-secondary-700"
            >
              Role
            </label>

            <Controller
              name="roleId"
              control={control}
              rules={{ required: 'Role is required' }}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={(val) => {
                    if (!val) return
                    field.onChange(val)
                    setValue(
                      'roleName',
                      (roles.find((r) => r.id === val)?.role_name as string) ||
                        ''
                    )
                  }}
                  fullWidth
                  placeholder="Select role"
                  options={availableRoles}
                  disabled={!availableRoles.length}
                />
              )}
            />
          </div>
        </form>
        <DialogFooter className="w-full">
          <Button
            type="button"
            className="rounded-md"
            onClick={() => handleOpenChange(false)}
            variant="outlined"
            color="default"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="invite-user-deliverable-set-form"
            className="rounded-md"
            variant="solid"
            color="primary"
            disabled={!!emailFormatError || !!isPending || !isValid}
            loading={isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const InviteUsersDialogMemorized = React.memo(InviteUsersDialog)
InviteUsersDialogMemorized.displayName = 'InviteUsersDialog'

export { InviteUsersDialogMemorized as InviteUsersDialog }
